import clsx from "clsx";
import { type ComponentProps, useState } from "react";
import { Autoplay, EffectFade, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import type { StrapiMedia } from "@shared/content-types/schemas/media.schema";
import { MediaBackground } from "@shared/ui/media-background";
import { SwiperNavButtons } from "@shared/ui/slider-nav-buttons";

import s from "./hero-banner-slider.module.scss";

export type HeroBannerSliderProps = ComponentProps<"div"> & {
  images: Array<StrapiMedia | null | undefined>;
};

export const HeroBannerSlider = (props: HeroBannerSliderProps) => {
  const { images, className, ...restProps } = props;
  const [activeIndex, setActiveIndex] = useState(0);

  const normalizedImages = (images || []).filter((m): m is StrapiMedia => !!m);
  const shouldShowControls = normalizedImages.length > 1;

  if (!normalizedImages.length) {
    return null;
  }

  return (
    <div className={clsx(s.root, className)} {...restProps}>
      <div className={s.sliderBlock}>
        <Swiper
          modules={[Autoplay, EffectFade, Pagination]}
          pagination={
            shouldShowControls
              ? {
                  el: `.${s.pagination}`,
                  type: "bullets",
                  clickable: true,
                  bulletClass: `${s.bullet}`,
                  bulletActiveClass: `${s.bulletActive}`,
                }
              : undefined
          }
          autoplay={
            shouldShowControls
              ? {
                  delay: 5000,
                  disableOnInteraction: false,
                }
              : undefined
          }
          className={s.slider}
          loop={shouldShowControls}
          effect="fade"
          fadeEffect={{ crossFade: true }}
          onSlideChange={({ realIndex }) => setActiveIndex(realIndex)}
        >
          {normalizedImages.map((media, index) => (
            <SwiperSlide
              key={`hero-banner-slide-${index + 1}`}
              className={s.slide}
            >
              <MediaBackground media={media} overlayClassName={s.noOverlay} />
            </SwiperSlide>
          ))}
          {shouldShowControls && (
            <>
              <SwiperNavButtons
                variant="light"
                buttonType="secondary"
                totalSlides={normalizedImages.length}
                currentIndex={activeIndex}
                className={s.buttons}
              />
              <div className={s.pagination} />
            </>
          )}
        </Swiper>
      </div>
    </div>
  );
};

HeroBannerSlider.displayName = "HeroBannerSlider";
